import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetCustomersShort, EntityStatus } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { getCustomersShortInfo } from 'backend/customers';
import { TEditUserSelectedItem } from '../../../../../editUserState/editUserContext';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

interface ICustomerSelect {
	setCustomer: (value: TEditUserSelectedItem) => void;
	setFetchedCustomers: (value: IItemForGetShortInfo[]) => void;
	selectedCustomer: TEditUserSelectedItem;
}

const CustomerSelect: React.FC<ICustomerSelect> = ({ setCustomer, setFetchedCustomers, selectedCustomer }) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([UserRoles.SUPER_USER, UserRoles.TECHNICAL]);
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(
		hasAccess ? getCustomersShortInfo : null,
	);
	const [showArchived, setShowArchived] = useState<boolean>(localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true');

	useEffect(() => {
		response && setFetchedCustomers(response.data.customers);
	}, [response]);

	const options = useMemo<ISelectOption[]>(() => {
		const responseData = response ? response.data.customers : [];
		const loggedUserData = loggedUser.customers || [];
		const items = hasAccess ? responseData : loggedUserData;
		const visibleItems = showArchived
			? items
			: items.filter((customer) => customer.status === EntityStatus.Active || customer.id === selectedCustomer);

		return visibleItems.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [hasAccess, response, loggedUser, showArchived, selectedCustomer]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<FormSelect
			testId="CustomerSelect"
			name="selectedCustomer"
			label="customerLabel"
			options={options}
			fetchError={responseError}
			loading={loading}
			onRefetch={refetch}
			onChange={setCustomer}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};

export default React.memo(CustomerSelect);
